import { CharacterData } from './character';

// ============================================================================
// TIER & LEVEL TYPES
// ============================================================================

/**
 * Daggerheart has 4 tiers that determine advancement options
 */
export type Tier = 1 | 2 | 3 | 4;

/**
 * Character levels range from 1-10
 */
export type Level = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

/**
 * Maps a level to its corresponding tier
 */
export const LEVEL_TO_TIER: Record<Level, Tier> = {
  1: 1,
  2: 2, 3: 2, 4: 2,
  5: 3, 6: 3, 7: 3,
  8: 4, 9: 4, 10: 4
};

/**
 * Determines which tier a level belongs to
 */
export function getTierForLevel(level: Level): Tier {
  return LEVEL_TO_TIER[level];
}

/**
 * Checks if a level triggers tier achievements
 */
export function isTierAchievementLevel(level: Level): boolean {
  return level === 2 || level === 5 || level === 8;
}

/**
 * Checks if a level enables multiclassing
 */
export function isMulticlassAvailable(level: Level): boolean {
  return level >= 5;
}

// ============================================================================
// CHARACTER TRAITS SYSTEM
// ============================================================================

/**
 * The 6 core character traits that can be increased and marked
 */
export type CharacterTrait =
  | 'agility'
  | 'strength'
  | 'finesse'
  | 'instinct'
  | 'presence'
  | 'knowledge';

/**
 * Tracks which traits have been marked (increased in current tier)
 * Marked traits cannot be increased again until tier achievement clears them
 */
export interface MarkedTraits {
  agility: boolean;
  strength: boolean;
  finesse: boolean;
  instinct: boolean;
  presence: boolean;
  knowledge: boolean;
}

/**
 * Creates a new MarkedTraits object with all traits unmarked
 */
export function createEmptyMarkedTraits(): MarkedTraits {
  return {
    agility: false,
    strength: false,
    finesse: false,
    instinct: false,
    presence: false,
    knowledge: false
  };
}

/**
 * Gets array of unmarked traits available for selection
 */
export function getUnmarkedTraits(markedTraits: MarkedTraits): CharacterTrait[] {
  return (Object.keys(markedTraits) as CharacterTrait[])
    .filter(trait => !markedTraits[trait]);
}

/**
 * Gets traits already selected in current level-up session's advancement choices
 */
export function getTraitsSelectedInSession(choices: AdvancementChoice[]): CharacterTrait[] {
  const selectedTraits: CharacterTrait[] = [];

  choices.forEach(choice => {
    if (choice.type === 'characterTraits') {
      selectedTraits.push(choice.trait1, choice.trait2);
    }
  });

  return selectedTraits;
}

/**
 * Gets traits available for selection considering both permanent marks and current session
 */
export function getAvailableTraitsForSelection(
  markedTraits: MarkedTraits,
  sessionChoices: AdvancementChoice[]
): CharacterTrait[] {
  const unmarked = getUnmarkedTraits(markedTraits);
  const selectedInSession = getTraitsSelectedInSession(sessionChoices);

  // Filter out traits already selected in this session
  return unmarked.filter(trait => !selectedInSession.includes(trait));
}

/**
 * Marks two traits and returns updated MarkedTraits object
 */
export function markTraits(
  current: MarkedTraits,
  trait1: CharacterTrait,
  trait2: CharacterTrait
): MarkedTraits {
  return {
    ...current,
    [trait1]: true,
    [trait2]: true
  };
}

/**
 * Clears all marked traits (happens at tier achievements)
 */
export function clearMarkedTraits(): MarkedTraits {
  return createEmptyMarkedTraits();
}

// ============================================================================
// EXPERIENCE SYSTEM
// ============================================================================

/**
 * Represents a single experience entry
 * Each experience has a name and a bonus value
 */
export interface ExperienceEntry {
  id: string; // Unique identifier for this experience
  name: string; // e.g., "Merchant", "Scholar", "Warrior"
  bonus: number; // Current bonus value (+1, +2, +3, etc.)
  createdAtLevel: Level; // Which level this experience was added
}

/**
 * Creates a new experience entry at +2 (tier achievement)
 */
export function createTierAchievementExperience(
  name: string,
  level: Level
): ExperienceEntry {
  return {
    id: generateId(),
    name,
    bonus: 2, // Tier achievements always create experience at +2
    createdAtLevel: level
  };
}

/**
 * Increases an existing experience by +1
 */
export function increaseExperience(experience: ExperienceEntry): ExperienceEntry {
  return {
    ...experience,
    bonus: experience.bonus + 1
  };
}

/**
 * Validates that an experience name is unique in the array
 */
export function isExperienceNameUnique(
  experiences: ExperienceEntry[],
  name: string,
  excludeId?: string
): boolean {
  return !experiences.some(exp =>
    exp.name.toLowerCase() === name.toLowerCase() && exp.id !== excludeId
  );
}

// ============================================================================
// ADVANCEMENT SLOT TRACKING
// ============================================================================

/**
 * Each advancement type has a specific number of slots per tier
 * This interface tracks which slots are marked (used)
 */
export interface AdvancementSlots {
  characterTraits: boolean[]; // 3 slots per tier
  hitPoints: boolean[]; // 2 slots per tier
  stress: boolean[]; // 2 slots per tier
  experiences: boolean[]; // 1 slot per tier
  domainCard: boolean[]; // 1 slot per tier
  evasion: boolean[]; // 1 slot per tier
  subclassUpgrade: boolean[]; // 1 slot per tier
  proficiency: boolean[]; // 2 slots per tier (black box)
  multiclass: boolean[]; // 2 slots per tier (black box)
}

/**
 * Tier-specific advancement tracking
 */
export interface TierAdvancementState {
  tier: Tier;
  slots: AdvancementSlots;
  /**
   * Tracks mutual exclusions - taking subclass upgrade disables multiclass
   * and vice versa within the same tier
   */
  crossedOut: {
    subclassUpgrade: boolean; // True if multiclass was taken
    multiclass: boolean; // True if subclass upgrade was taken
  };
}

/**
 * Creates empty advancement slots for a tier
 */
export function createEmptyAdvancementSlots(): AdvancementSlots {
  return {
    characterTraits: [false, false, false],
    hitPoints: [false, false],
    stress: [false, false],
    experiences: [false],
    domainCard: [false],
    evasion: [false],
    subclassUpgrade: [false],
    proficiency: [false, false],
    multiclass: [false, false]
  };
}

/**
 * Gets the number of unmarked (available) slots for an advancement
 */
export function getAvailableSlots(slots: boolean[]): number {
  return slots.filter(slot => !slot).length;
}

/**
 * Marks the next available slot for an advancement
 * Returns updated array or null if no slots available
 */
export function markNextSlot(slots: boolean[]): boolean[] | null {
  const index = slots.findIndex(slot => !slot);
  if (index === -1) return null;

  const updated = [...slots];
  updated[index] = true;
  return updated;
}

/**
 * Marks N slots for an advancement (e.g., 2 slots for proficiency)
 * Returns updated array or null if not enough slots available
 */
export function markNSlots(slots: boolean[], count: number): boolean[] | null {
  if (getAvailableSlots(slots) < count) return null;

  const updated = [...slots];
  let marked = 0;
  for (let i = 0; i < updated.length && marked < count; i++) {
    if (!updated[i]) {
      updated[i] = true;
      marked++;
    }
  }
  return updated;
}

/**
 * Unmarks the last marked slot for an advancement (for removal)
 * Returns updated array or null if no marked slots
 */
export function unmarkSlot(slots: boolean[]): boolean[] | null {
  // Find the last marked slot (rightmost true)
  const index = slots.lastIndexOf(true);
  if (index === -1) return null;

  const updated = [...slots];
  updated[index] = false;
  return updated;
}

/**
 * Checks if an advancement has at least one unmarked slot
 */
export function hasAvailableSlot(slots: boolean[]): boolean {
  return slots.some(slot => !slot);
}

/**
 * Checks if an advancement has at least N unmarked slots
 */
export function hasNAvailableSlots(slots: boolean[], count: number): boolean {
  return getAvailableSlots(slots) >= count;
}

// ============================================================================
// TIER ACHIEVEMENT STATE
// ============================================================================

/**
 * Represents the state of tier achievements applied during level-up
 * Only applies at levels 2, 5, and 8
 */
export interface TierAchievementState {
  level: Level;
  newExperience: ExperienceEntry | null;
  proficiencyIncreased: boolean;
  traitsCleared: boolean; // Only true at levels 5 and 8
}

/**
 * Creates a tier achievement state for a given level
 */
export function createTierAchievement(level: Level): TierAchievementState {
  if (!isTierAchievementLevel(level)) {
    throw new Error(`Level ${level} does not have tier achievements`);
  }

  return {
    level,
    newExperience: null, // Will be set by user during wizard
    proficiencyIncreased: true, // Always true - tier achievements always grant +1 proficiency
    traitsCleared: level === 5 || level === 8 // Clear traits at levels 5 and 8
  };
}

// ============================================================================
// ADVANCEMENT CHOICES
// ============================================================================

/**
 * Represents a single advancement choice made during level-up
 */
export type AdvancementChoice =
  | { type: 'characterTraits'; trait1: CharacterTrait; trait2: CharacterTrait }
  | { type: 'hitPoints'; count: 1 }
  | { type: 'stress'; count: 1 }
  | { type: 'experience'; experienceId: string }
  | { type: 'domainCard'; cardId: string }
  | { type: 'evasion' }
  | { type: 'subclassUpgrade'; cardId: string }
  | { type: 'proficiency' }
  | { type: 'multiclass'; classId: string; domainId: string; foundationCardId: string };

/**
 * Validates an advancement choice against current character state
 */
export function validateAdvancementChoice(
  choice: AdvancementChoice,
  character: CharacterData,
  tierState: TierAdvancementState,
  sessionChoices: AdvancementChoice[] = [],
  tierAchievementExperience?: ExperienceEntry | null,
  tierAchievementTraitsCleared?: boolean,
  targetLevel?: Level // Level being leveled UP TO
): { valid: boolean; error?: string } {
  switch (choice.type) {
    case 'characterTraits': {
      // Use empty marks if tier achievement clears traits, otherwise use character's marks
      const markedTraits = tierAchievementTraitsCleared
        ? createEmptyMarkedTraits()
        : (character.markedTraits || createEmptyMarkedTraits());

      const unmarked = getUnmarkedTraits(markedTraits);
      const selectedInSession = getTraitsSelectedInSession(sessionChoices);

      // Check if traits are permanently marked
      if (!unmarked.includes(choice.trait1)) {
        return { valid: false, error: `${choice.trait1} is already marked` };
      }
      if (!unmarked.includes(choice.trait2)) {
        return { valid: false, error: `${choice.trait2} is already marked` };
      }

      // Check if traits are already selected in current session
      if (selectedInSession.includes(choice.trait1)) {
        return { valid: false, error: `${choice.trait1} already selected in this level-up` };
      }
      if (selectedInSession.includes(choice.trait2)) {
        return { valid: false, error: `${choice.trait2} already selected in this level-up` };
      }

      if (!hasAvailableSlot(tierState.slots.characterTraits)) {
        return { valid: false, error: 'No character trait slots available' };
      }
      return { valid: true };
    }

    case 'hitPoints':
      if (!hasAvailableSlot(tierState.slots.hitPoints)) {
        return { valid: false, error: 'No hit point slots available' };
      }
      return { valid: true };

    case 'stress':
      if (!hasAvailableSlot(tierState.slots.stress)) {
        return { valid: false, error: 'No stress slots available' };
      }
      return { valid: true };

    case 'experience': {
      // Check both character experiences and tier achievement experience
      const experience = character.experiences?.find(e => e.id === choice.experienceId) ||
                        (tierAchievementExperience?.id === choice.experienceId
                          ? tierAchievementExperience
                          : null);
      if (!experience) {
        return { valid: false, error: 'Experience not found' };
      }
      if (!hasAvailableSlot(tierState.slots.experiences)) {
        return { valid: false, error: 'No experience slots available' };
      }
      return { valid: true };
    }

    case 'domainCard':
      if (!hasAvailableSlot(tierState.slots.domainCard)) {
        return { valid: false, error: 'No domain card slots available' };
      }
      return { valid: true };

    case 'evasion':
      if (!hasAvailableSlot(tierState.slots.evasion)) {
        return { valid: false, error: 'No evasion slots available' };
      }
      return { valid: true };

    case 'subclassUpgrade':
      if (tierState.crossedOut.subclassUpgrade) {
        return { valid: false, error: 'Subclass upgrade disabled (multiclass taken in tier)' };
      }
      if (!hasAvailableSlot(tierState.slots.subclassUpgrade)) {
        return { valid: false, error: 'No subclass upgrade slots available' };
      }
      return { valid: true };

    case 'proficiency':
      if (!hasNAvailableSlots(tierState.slots.proficiency, 2)) {
        return { valid: false, error: 'Proficiency requires 2 advancement slots' };
      }
      return { valid: true };

    case 'multiclass':
      if (tierState.crossedOut.multiclass) {
        return { valid: false, error: 'Multiclass disabled (subclass upgrade taken in tier)' };
      }
      if (!hasNAvailableSlots(tierState.slots.multiclass, 2)) {
        return { valid: false, error: 'Multiclass requires 2 advancement slots' };
      }
      // Use target level if provided (level being leveled TO), otherwise fall back to current level
      const levelToCheck = targetLevel || (parseInt(character.level) as Level);
      if (!isMulticlassAvailable(levelToCheck)) {
        return { valid: false, error: 'Multiclass only available at level 5+' };
      }
      return { valid: true };

    default:
      return { valid: false, error: 'Unknown advancement type' };
  }
}

/**
 * Calculates the slot cost for an advancement choice
 */
export function getAdvancementSlotCost(choice: AdvancementChoice): number {
  switch (choice.type) {
    case 'proficiency':
    case 'multiclass':
      return 2; // Black box advancements cost 2 slots
    default:
      return 1;
  }
}

// ============================================================================
// DOMAIN CARD OPERATIONS
// ============================================================================

/**
 * Represents a domain card exchange during level-up
 * Player can exchange one existing card for a new one (optional)
 */
export interface DomainCardExchange {
  oldCardId: string;
  newCardId: string;
}

/**
 * Validates a domain card selection for level-up
 */
export function validateDomainCardLevel(
  cardLevel: number,
  characterLevel: Level,
  isMulticlassDomain: boolean
): { valid: boolean; error?: string } {
  const maxLevel = isMulticlassDomain
    ? Math.ceil(characterLevel / 2) // Multiclass cards: half level rounded up
    : characterLevel; // Base class cards: at or below character level

  if (cardLevel > maxLevel) {
    return {
      valid: false,
      error: `Card level ${cardLevel} exceeds maximum ${maxLevel}${isMulticlassDomain ? ' (multiclass)' : ''}`
    };
  }

  return { valid: true };
}

/**
 * Gets the maximum card level available for selection
 */
export function getMaxDomainCardLevel(
  characterLevel: Level,
  isMulticlassDomain: boolean
): number {
  return isMulticlassDomain
    ? Math.ceil(characterLevel / 2)
    : characterLevel;
}

// ============================================================================
// LEVEL-UP SESSION STATE
// ============================================================================

/**
 * Represents the complete state of a level-up wizard session
 * This is temporary state that gets committed to the character on completion
 */
export interface LevelUpSession {
  // Basic info
  sessionId: string;
  characterId: string;
  fromLevel: Level;
  toLevel: Level;
  tier: Tier;

  // Step 1: Tier Achievements (only at levels 2, 5, 8)
  tierAchievement: TierAchievementState | null;

  // Step 2: Advancement Choices (choose 2)
  advancementChoices: AdvancementChoice[];

  // Step 3: Damage Thresholds (automatic +1)
  damageThresholdsIncreased: boolean;

  // Step 4: Domain Cards (required + optional exchange)
  newDomainCard: string | null; // Required: new card ID
  newDomainCards: string[]; // Multiple cards when hasAdditionalDomainCard is true
  domainCardExchange: DomainCardExchange | null; // Optional: exchange
  hasAdditionalDomainCard: boolean; // Flag: user selected additional domain card in advancements

  // Tracking
  tierAdvancementState: TierAdvancementState;
  createdAt: number; // Timestamp
  completedAt: number | null; // Timestamp when completed
}

/**
 * Creates a new level-up session
 */
export function createLevelUpSession(
  characterId: string,
  character: CharacterData
): LevelUpSession {
  const fromLevel = parseInt(character.level) as Level;
  const toLevel = (fromLevel + 1) as Level;
  const tier = getTierForLevel(toLevel);

  // Load existing tier advancement state or create new
  const existingTierState = character.tierAdvancements?.[tier];
  const tierAdvancementState: TierAdvancementState = existingTierState || {
    tier,
    slots: createEmptyAdvancementSlots(),
    crossedOut: {
      subclassUpgrade: false,
      multiclass: false
    }
  };

  return {
    sessionId: generateId(),
    characterId,
    fromLevel,
    toLevel,
    tier,
    tierAchievement: isTierAchievementLevel(toLevel)
      ? createTierAchievement(toLevel)
      : null,
    advancementChoices: [],
    damageThresholdsIncreased: false,
    newDomainCard: null,
    newDomainCards: [],
    domainCardExchange: null,
    hasAdditionalDomainCard: false,
    tierAdvancementState,
    createdAt: Date.now(),
    completedAt: null
  };
}

/**
 * Validates that a level-up session is complete and ready to commit
 */
export function validateLevelUpSession(session: LevelUpSession): {
  valid: boolean;
  errors: string[]
} {
  const errors: string[] = [];

  // Check tier achievement completion (if applicable)
  if (session.tierAchievement && !session.tierAchievement.newExperience) {
    errors.push('Tier achievement requires a new experience');
  }

  // Check advancement choices (must have exactly 2 slots worth)
  const slotsUsed = session.advancementChoices.reduce(
    (total, choice) => total + getAdvancementSlotCost(choice),
    0
  );
  if (slotsUsed !== 2) {
    errors.push(`Must use exactly 2 advancement slots (used ${slotsUsed})`);
  }

  // Check damage thresholds increased
  if (!session.damageThresholdsIncreased) {
    errors.push('Damage thresholds must be increased');
  }

  // Check new domain cards selected
  const requiredCardCount = session.hasAdditionalDomainCard ? 2 : 1;
  if (!session.newDomainCards || session.newDomainCards.length !== requiredCardCount) {
    errors.push(`Must select ${requiredCardCount} domain card${requiredCardCount > 1 ? 's' : ''}`);
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

// ============================================================================
// LEVEL-UP CHANGES
// ============================================================================

/**
 * Represents the changes applied by a level-up
 */
export interface LevelUpChanges {
  levelIncreased: boolean;
  tierAchievement?: {
    newExperience: ExperienceEntry;
    proficiencyIncreased: boolean;
    traitsCleared: boolean;
  };
  advancements: {
    characterTraitsIncreased?: { trait1: CharacterTrait; trait2: CharacterTrait };
    hitPointsGained?: number;
    stressGained?: number;
    experienceIncreased?: string; // experience ID
    domainCardAcquired?: string; // card ID
    evasionIncreased?: boolean;
    subclassUpgraded?: string; // card ID
    proficiencyIncreased?: boolean;
    multiclassAcquired?: {
      classId: string;
      domainId: string;
      foundationCardId: string;
    };
  };
  damageThresholdsIncreased: boolean;
  newDomainCard: string;
  domainCardExchanged?: DomainCardExchange;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculates total advancement slots available at current level
 */
export function getTotalAdvancementSlotsAvailable(
  session: LevelUpSession
): number {
  return 2; // Always 2 advancement choices per level
}

/**
 * Calculates how many slots have been used
 */
export function getAdvancementSlotsUsed(
  choices: AdvancementChoice[]
): number {
  return choices.reduce((total, choice) =>
    total + getAdvancementSlotCost(choice), 0
  );
}

/**
 * Calculates remaining advancement slots
 */
export function getRemainingAdvancementSlots(
  session: LevelUpSession
): number {
  const total = getTotalAdvancementSlotsAvailable(session);
  const used = getAdvancementSlotsUsed(session.advancementChoices);
  return total - used;
}

/**
 * Checks if player can add another advancement choice
 */
export function canAddAdvancementChoice(
  session: LevelUpSession,
  choice: AdvancementChoice
): boolean {
  const remaining = getRemainingAdvancementSlots(session);
  const cost = getAdvancementSlotCost(choice);
  return remaining >= cost;
}

/**
 * Saves level-up session to localStorage (temporary storage during wizard)
 */
export function saveLevelUpSession(session: LevelUpSession): void {
  localStorage.setItem(
    `levelup_session_${session.characterId}`,
    JSON.stringify(session)
  );
}

/**
 * Loads level-up session from localStorage
 */
export function loadLevelUpSession(characterId: string): LevelUpSession | null {
  const data = localStorage.getItem(`levelup_session_${characterId}`);
  return data ? JSON.parse(data) : null;
}

/**
 * Clears level-up session from localStorage
 */
export function clearLevelUpSession(characterId: string): void {
  localStorage.removeItem(`levelup_session_${characterId}`);
}

/**
 * Generates a unique ID
 */
function generateId(): string {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
